/**
 * The print agent's localhost endpoint (same PC as this browser). Opt-in per device: probing
 * 127.0.0.1 from tablets without an agent would only cause errors and, in newer Chrome, a
 * "local network access" permission prompt.
 */
const KEY = 'shripos.localAgent';

export function localAgentSetting() {
  try {
    const v = JSON.parse(localStorage.getItem(KEY) ?? 'null');
    return v && v.on ? { on: true, port: Number(v.port) || 9123 } : { on: false, port: 9123 };
  } catch {
    return { on: false, port: 9123 };
  }
}

export function setLocalAgentSetting(on, port = 9123) {
  try {
    localStorage.setItem(KEY, JSON.stringify({ on, port }));
  } catch {
    // Private mode: the setting lasts for this page only.
  }
  cached = null;
}

let cached = null; // { at, status }

async function call(path, init = {}, timeoutMs = 2_000) {
  const { port } = localAgentSetting();
  const ctrl = new AbortController();
  const t = setTimeout(() => ctrl.abort(), timeoutMs);
  try {
    return await fetch(`http://127.0.0.1:${port}${path}`, {
      ...init,
      signal: ctrl.signal,
      targetAddressSpace: 'loopback',
    });
  } finally {
    clearTimeout(t);
  }
}

/** The local agent's status, or null when it isn't running / not enabled. Cached for 30 s. */
export async function localAgentStatus({ force = false } = {}) {
  if (!localAgentSetting().on) return null;
  if (!force && cached && Date.now() - cached.at < 30_000) return cached.status;
  let status;
  try {
    const res = await call('/v1/status');
    status = res.ok ? await res.json() : null;
  } catch {
    status = null;
  }
  cached = { at: Date.now(), status };
  return status;
}

export async function printViaLocalAgent(printerId, base64, label) {
  const res = await call(
    '/v1/print',
    {
      method: 'POST',
      headers: { 'Content-Type': 'application/json' },
      body: JSON.stringify({ printerId, data: base64, label }),
    },
    30_000,
  );
  const body = await res.json().catch(() => ({}));
  if (!res.ok || !body.ok) throw new Error(body.error ?? `Local print agent error (${res.status})`);
  return body;
}

/** Offline kitchen tickets to this PC's print agent, for its kitchen screen on the shop's Wi-Fi. */
export async function sendKitchenToLocalAgent(tickets) {
  const res = await call('/v1/kitchen/tickets', {
    method: 'POST',
    headers: { 'Content-Type': 'application/json' },
    body: JSON.stringify({ tickets }),
  });
  if (!res.ok) throw new Error(`Local print agent error (${res.status})`);
  return res.json();
}
